import BasePlatformFeatureList from 'components/download-page/BasePlatformFeatureList'
import BaseHeader from 'components/download-page/BasePlatformHeader'
import DocumentationLinks from 'components/download-page/DocumentationLinks'
import DownloadSeparator from 'components/download-page/DownloadSeparator'
import IosDownloadOptions from 'components/download-page/IosDownloadOptions'
import PlatformDownloadSection from 'components/download-page/PlatformDownloadSection'
import PlatformSelector from 'components/download-page/PlatformSelector'
import PlatformTitle from 'components/download-page/PlatformTitle'
import Tag from 'components/Tag'
import ExternalLink from 'components/UI/ExternalLink'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import GetOnAmazon from 'images/amazon-icon.png'

import GetOnGooglePlay from 'images/get-on-google-play.png'
import { baseImageStyle } from 'pages/download'
import { Dispatch, SetStateAction } from 'react'
import { Flex } from 'theme-ui'
import { AvailablePlatforms } from 'utils/downloadPageUtils'
import getEnglishStrings from 'utils/getEnglishStrings'

export default function TVPlatform({
  platformName,
  setPlatform,
}: {
  platformName: AvailablePlatforms
  setPlatform: Dispatch<SetStateAction<AvailablePlatforms>>
}): JSX.Element {
  const translations = useStaticQuery<Queries.TVPlatformQuery>(graphql`
    query TVPlatform {
      directus {
        download_page {
          strings {
            languages_code {
              code
            }
            windscribe_for_firetv
            windscribe_for_androidtv
            windscribe_for_appletv
            servers_in_over
            six_different_protocols
            network_and_per_app
            customizable_in_app
          }
        }
        general_translations {
          strings {
            languages_code {
              code
            }
            tv
            or
          }
        }
      }
    }
  `)

  const downloadPageStrings = getEnglishStrings(translations.directus.download_page?.strings)
  const generalTranslationStrings = getEnglishStrings(
    translations.directus.general_translations?.strings,
  )
  return (
    <BaseHeader>
      <Tag title={generalTranslationStrings?.tv ?? ''} />
      <PlatformSelector
        platformName={platformName}
        setPlatform={setPlatform}
        availablePlatforms={['FireTV', 'AndroidTV', 'AppleTV']}
      />
      <Flex
        sx={{
          justifyContent: 'space-between',
          flexDirection: ['column-reverse', 'row', 'row'],
          alignItems: ['center', 'flex-start'],
          width: '100%',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: ['3.2rem', '4.8rem'],
            flexGrow: 1,
            minWidth: ['auto', '38.5rem'],
          }}
        >
          <PlatformTitle
            title={
              {
                FireTV: downloadPageStrings?.windscribe_for_firetv ?? '',
                AndroidTV: downloadPageStrings?.windscribe_for_androidtv ?? '',
                AppleTV: downloadPageStrings?.windscribe_for_appletv ?? '',
              }[platformName]
            }
          />
          {/* Download Section */}
          <Flex
            sx={{
              flexDirection: 'column',
              width: ['auto', '40rem'],
              gap: '2.4rem',
              alignItems: ['center', 'flex-start'],
            }}
          >
            {
              {
                AndroidTV: (
                  <ExternalLink
                    href="https://play.google.com/store/apps/details?id=com.windscribe.vpn"
                    sxx={{ display: 'flex', justifyContent: ['center', 'center', 'flex-start'] }}
                  >
                    <img src={GetOnGooglePlay} alt="" sx={{ width: '24.8rem', height: '100%' }} />
                  </ExternalLink>
                ),
                FireTV: (
                  <ExternalLink
                    href="http://www.amazon.com/gp/mas/dl/android?p=com.windscribe.vpn"
                    sxx={{ display: 'flex', justifyContent: ['center', 'center', 'flex-start'] }}
                  >
                    <img src={GetOnAmazon} alt="" sx={{ width: '24.8rem', height: '100%' }} />
                  </ExternalLink>
                ),
              }[platformName]
            }
            {platformName === 'AppleTV' ? (
              <IosDownloadOptions />
            ) : (
              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: '2.4rem',
                  maxWidth: ['100%', '100%', '26.8rem'],
                }}
              >
                <DownloadSeparator or={generalTranslationStrings?.or} />
                <PlatformDownloadSection platformName={platformName} />
              </Flex>
            )}
          </Flex>
          <Flex sx={{ flexDirection: 'column', gap: '2.4rem' }}>
            <BasePlatformFeatureList
              featureList={
                platformName === 'AppleTV'
                  ? [
                      downloadPageStrings?.servers_in_over ?? '',
                      downloadPageStrings?.six_different_protocols ?? '',
                      downloadPageStrings?.customizable_in_app ?? '',
                    ]
                  : [
                      downloadPageStrings?.servers_in_over ?? '',
                      downloadPageStrings?.six_different_protocols ?? '',
                      downloadPageStrings?.network_and_per_app ?? '',
                    ]
              }
            />
            <DocumentationLinks platformName={platformName} />
          </Flex>
        </Flex>
        <Flex sx={{ maxWidth: '45.8rem' }}>
          {
            <>
              <StaticImage
                src="../../images/windscribe-tv-android.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...baseImageStyle,
                  display: platformName === 'AndroidTV' ? 'block' : 'none',
                  height: ['auto', '33.5rem', '49.6rem'],
                }}
                imgStyle={{ objectFit: 'contain', height: 'fit-content' }}
              />
              <StaticImage
                src="../../images/windscribe-tv-firetv.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...baseImageStyle,
                  display: platformName === 'FireTV' ? 'block' : 'none',
                  height: ['auto', '33.5rem', '49.6rem'],
                }}
                imgStyle={{ objectFit: 'contain', height: 'fit-content' }}
              />
              <StaticImage
                src="../../images/windscribe-apple-tv.png"
                alt={''}
                placeholder="blurred"
                loading="eager"
                sx={{
                  ...baseImageStyle,
                  display: platformName === 'AppleTV' ? 'block' : 'none',
                  height: ['auto', '33.5rem', '49.6rem'],
                }}
                imgStyle={{ objectFit: 'contain', height: 'fit-content' }}
              />
            </>
          }
        </Flex>
      </Flex>
    </BaseHeader>
  )
}
